import { Typography } from '@mui/material'
import { useMemo } from 'react'

import { Artist, Maybe } from '@/clients/utoniq-core/schema'
import { Head } from '@/components/layouts'
import { ConsoleBaseLayout } from '@/components/layouts/ConsoleBaseLayout'
import { ArtistThemeProvider } from '@/components/styles'
import { useRouter, useTranslation } from '@/core'
import { Cell, CellFrame, Template } from '@/core/architecture'

import { BankEditForm, BankEditFormProps } from './BankEditForm'

export * from './fragments'

export type State = {
  artistId: string
  artist: Maybe<Pick<Artist, 'id' | 'bank'>>
}

type CurrentAccountCellProps = {
  artist: State['artist']
}

/**
 * Action type.
 * use key name with 'on' prefix like onLogin.
 */
export type Action = {
  onSubmit: BankEditFormProps['onSubmit']
}

export const ConsoleBankEditTemplate: Template<State, Action> = ({
  state,
  onSubmit,
}) => {
  const { routerNames, routerPages } = useRouter()
  const { t } = useTranslation('consoleBankEdit')

  return (
    <ArtistThemeProvider artistId={state.artistId}>
      <Head title={routerNames.ConsoleBankEdit} />

      <ConsoleBaseLayout
        hasConsoleFooter
        header={{
          back: true,
          defaultBackPage: routerPages.ConsoleAccountSetting,
          title: t('title'),
        }}
      >
        <CurrentAccountCell artist={state.artist} />
        <BankEditForm onSubmit={onSubmit} />
      </ConsoleBaseLayout>
    </ArtistThemeProvider>
  )
}

const CurrentAccountCell: Cell<CurrentAccountCellProps> = ({ artist }) => {
  const { t } = useTranslation('consoleBankEdit')
  const bankInfo = useMemo<string | undefined>(() => {
    if (!artist?.bank) {
      return
    }
    const { bankName, routingNumber, accountHolderName, last4 } = artist.bank
    return [
      bankName,
      `${routingNumber.slice(4)} ***${last4}`,
      accountHolderName,
    ].join('\n')
  }, [artist?.bank])
  return (
    <CellFrame sx={{ px: 5, pt: 5, pb: 8 }}>
      <Typography variant="h5">{t('registeredAccountInfo')}</Typography>
      <Typography variant="body2" sx={{ pt: 5 }}>
        {bankInfo ?? t('unregistered')}
      </Typography>
    </CellFrame>
  )
}
